import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Text, textVariantsEnum, fontWeightEnum } from '../../components/Text';
const SubscriptionCard = ({
  currentActivePlanId,
  currentInActivePlanId,
  showCycleEndWarning,
  subscriptionEndDate,
  cancelledAt,
  plan,
  billingCycle,
  currencyCode,
  isSelected,
  onSelect,
  onSubscribe,
  isLoading,
  onCancelPlan,
  onContactUs,
  onModifySeats,
}) => {
  const subscriptionData = useSelector(
    (state) => state.auth?.profile?.subscription
  );
  const isSeatsExhausted = useMemo(() => {
    return (
      subscriptionData?.occupiedSeats >= subscriptionData?.seats &&
      subscriptionData?.seatsExhausted
    );
  }, [subscriptionData]);
  // Get price based on billing cycle
  const getPrice = () => {
    const pricing = plan.pricing?.[currencyCode] || plan.pricing?.USD || {};
    switch (billingCycle) {
      case 'quarterly':
        return pricing.quarterly;
      case 'yearly':
        return pricing.yearly;
      default:
        return pricing.monthly;
    }
  };

  const getCurrencySymbol = () => {
    if (currencyCode === 'INR') return 'Rs.';
    return '$';
  };

  // Get billing period text
  const getBillingPeriod = () => {
    let billingPeriod = '/ user ';
    switch (billingCycle) {
      case 'quarterly':
        billingPeriod += '/ quarter';
        break;
      case 'yearly':
        billingPeriod += '/ year';
        break;
      default:
        billingPeriod += '/ month';
    }

    return billingPeriod;
  };

  // Calculate savings
  const getSavings = () => {
    const monthlyPrice = plan.pricing?.[currencyCode]?.monthly;
    const currentPrice = getPrice();
    if (!monthlyPrice || !currentPrice) return 0;
    const cycleFactor =
      billingCycle === 'quarterly' ? 3 : billingCycle === 'yearly' ? 12 : 1;
    const currentMonthlyEquivalent = currentPrice / cycleFactor;
    const savings =
      ((monthlyPrice - currentMonthlyEquivalent) / monthlyPrice) * 100;
    return savings > 0 ? Math.round(savings) : 0;
  };
  const isCurrentPlanPurchased =
    (typeof currentActivePlanId === 'string' &&
      currentActivePlanId === plan.razorpayPlanId[billingCycle]) ||
    (typeof currentInActivePlanId === 'string' &&
      currentInActivePlanId === plan.razorpayPlanId[billingCycle]);

  const showCancelButton =
    typeof currentActivePlanId === 'string' && isCurrentPlanPurchased;
  const getTextToDisplay = () => {
    if (isCurrentPlanPurchased) {
      return 'Current Plan';
    }
    if (typeof currentActivePlanId === 'string') {
      return 'Change Plan';
    }
    return 'Choose Plan';
  };
  return (
    <div
      className={`subscription-card ${isSelected ? 'selected' : ''} ${plan.popular ? 'popular' : ''}`}
      onClick={() => {
        if (isCurrentPlanPurchased) {
          return;
        }
        onSelect();
      }}
    >
      {/* Popular Badge */}
      {plan.popular && (
        <div className="popular-badge">
          <span>Most Popular</span>
        </div>
      )}

      {/* Card Header */}
      <div className="card-header">
        <div className="plan-icon" style={{ background: plan.gradient }}>
          <i className={plan.icon}></i>
        </div>
        <div className="plan-info">
          <h3 className="plan-name">{plan.name}</h3>
          <p className="plan-description">{plan.description}</p>
        </div>
      </div>

      {/* Pricing */}
      {plan.id !== 'enterprise' && (
        <div className="pricing-section">
          <div className="price-container">
            <span className="currency">{getCurrencySymbol()}</span>
            <span className="price">{getPrice()}</span>
            <span className="period">{getBillingPeriod()}</span>
          </div>
          <div className="container">
            <span className="period">Minimum 2 users</span>
          </div>
          {getSavings() > 0 && (
            <div className="savings-badge">
              <span>Save {getSavings()}%</span>
            </div>
          )}
        </div>
      )}

      {/* Features List */}
      <div className="features-section">
        <ul className="features-list">
          {plan.features.map((feature, index) => (
            <li key={index} className="feature-item">
              <i className="ni ni-check-bold"></i>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Action Button */}
      {plan.id !== 'enterprise' && !isCurrentPlanPurchased && (
        <div className="card-actions">
          <button
            className={`subscribe-button selected`}
            onClick={(e) => {
              if (isLoading) {
                return;
              }

              onSubscribe();
            }}
            disabled={isLoading}
            style={{ background: plan.gradient }}
          >
            {isLoading && isSelected ? (
              <div className="loading-spinner">
                {/* <div className="spinner"></div> */}
                {/* <span>Processing...</span> */}
              </div>
            ) : (
              <>
                {getTextToDisplay()}

                <i className="ni ni-bold-right"></i>
              </>
            )}
          </button>
        </div>
      )}
      {isCurrentPlanPurchased && isSeatsExhausted && (
        <div className="card-actions mt-4">
          <button
            className={`modify-seats-button`}
            disabled={isLoading}
            style={{ background: 'transparent' }}
            onClick={onModifySeats}
          >
            Modify Seats
          </button>
        </div>
      )}
      {isCurrentPlanPurchased && (
        <div className="card-actions mt-4">
          <button
            className={`current-button`}
            disabled={isLoading}
            style={{ background: 'transparent' }}
          >
            Current Plan
          </button>
        </div>
      )}
      {isCurrentPlanPurchased && showCycleEndWarning && (
        <div className="mt-4 note-container">
          <Text
            variant={textVariantsEnum.Text_md}
            fontWeight={fontWeightEnum.Medium}
            color="primary-500"
          >
            Your subscription cycle ends on:
          </Text>
          <Text
            variant={textVariantsEnum.Text_md}
            fontWeight={fontWeightEnum.Semibold}
            color="primary-500"
          >
            {subscriptionEndDate}
          </Text>
        </div>
      )}
      {plan.id === 'enterprise' && (
        <div className="card-actions mt-4">
          <button
            className={`subscribe-button`}
            disabled={isLoading}
            style={{ background: 'transparent' }}
            onClick={onContactUs}
          >
            Contact Us
          </button>
        </div>
      )}
      {showCancelButton && (
        <div className="card-actions mt-4">
          <button
            className={`cancel-button`}
            disabled={isLoading}
            style={{ background: 'transparent' }}
            onClick={onCancelPlan}
          >
            Cancel Plan
          </button>
        </div>
      )}

      {/* Selection Indicator */}
      {isSelected && (
        <div className="selection-indicator">
          <i className="ni ni-check-bold"></i>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
